import axiosMiddleware from "./util/AxiosMiddleware";
import { axiosInstance } from "./util/AxiosInstance";
import { DRLog } from "./util/Logging";
import { getDRErrorValue } from "./util/StateManagement";
import { isValidString, isResponseValid } from "./util/ValidationHelpers";

axiosInstance.interceptors.request.use(axiosMiddleware);

// Log all Axios responses
axiosInstance.interceptors.response.use(
  (response) => {
    const logDetails = {
      request_method: response.config.method,
      request_params: response.config.params || null,
      status: response.status,
      url: response.config.baseURL + response.config.url,
      data: JSON.stringify(response.data).substring(0, 200),
      cached: response.cached || false
    };
    DRLog("DEBUG", `HTTP RESPONSE: ${JSON.stringify(logDetails)}`);
    return response;
  },
  (error) => {
    DRLog("ERROR", `HTTP RESPONSE ERROR: ${error}`);
    return Promise.reject(error);
  }
);

export async function AccountDCCode(Account_No: string): Promise<string> {
  try {
    const response = await axiosInstance.get("/api/DR/draccountdata");
    if (!isResponseValid(response)) {
      const drErrorValue = getDRErrorValue();
      return isValidString(drErrorValue) ? drErrorValue : "0";
    } else if (response.data.responseMessage?.code === 2) {
      return "0";
    } else {
      // Pick AccountDcCode for the given Account_No (case-insensitive lookup)
      const accountData = response.data.DrAccountData;
      const normalizedAccountNo = Account_No.toUpperCase();
      const accountKey =
        accountData && Object.prototype.hasOwnProperty.call(accountData, Account_No)
          ? Account_No
          : Object.keys(accountData || {}).find((key) => key.toUpperCase() === normalizedAccountNo);
      if (accountKey && accountData[accountKey]?.AccountDcCode !== undefined) {
        return String(accountData[accountKey].AccountDcCode);
      } else {
        return "0";
      }
    }
  } catch (error) {
    DRLog("ERROR", "AccountDCCode " + error);
    throw error;
  }
}

export async function AccountName(Account_No: string, Language_Code: string): Promise<string> {
  try {
    const response = await axiosInstance.get("/api/DR/draccountdata");
    if (!isResponseValid(response)) {
      const drErrorValue = getDRErrorValue();
      return isValidString(drErrorValue) ? drErrorValue : "0";
    } else if (response.data.responseMessage?.code === 2) {
      return "0";
    } else {
      // Pick account name from LocalizationInfo for the given Account_No and Language_Code (case-insensitive lookup)
      const accountData = response.data.DrAccountData;
      const normalizedAccountNo = Account_No.toUpperCase();
      const accountKey =
        accountData && Object.prototype.hasOwnProperty.call(accountData, Account_No)
          ? Account_No
          : Object.keys(accountData || {}).find((key) => key.toUpperCase() === normalizedAccountNo);
      if (
        accountKey && accountData[accountKey]?.LocalizationInfo?.[Language_Code]
      ) {
        return accountData[accountKey].LocalizationInfo[Language_Code];
      } else {
        return "0";
      }
    }
  } catch (error) {
    DRLog("ERROR", "AccountName error" + error);
    throw error;
  }
}

export async function DatatypeName(Datatype_Code: string, Language_Code: string): Promise<string> {
  try {
    const response = await axiosInstance.get("/api/DR/datatypename", {
      params: {
        dataTypeCode: Datatype_Code,
        langCode: Language_Code,
      },
    });
    if (!isResponseValid(response)) {
      const drErrorValue = getDRErrorValue();
      return isValidString(drErrorValue) ? drErrorValue : "0";
    } else if (response.data.responseMessage?.code === 2) {
      return "0";
    } else {
      return response.data.datatypeName;
    }
  } catch (error) {
    DRLog("ERROR", "DatatypeName error" + error);
    throw error;
  }
}

export async function CurrencyRate(
  Currency: string,
  Rate_Type: 10 | 20 | 30,
  Datatype_Code: string,
  Year: number,
  Period: number
): Promise<string> {
  try {
    const response = await axiosInstance.get("/api/DR/currencyrate", {
      params: {
        currency: Currency,
        rateType: Rate_Type,
        datatypeCode: Datatype_Code,
        year: Year,
        period: Period,
      },
    });
    if (!isResponseValid(response)) {
      const drErrorValue = getDRErrorValue();
      return isValidString(drErrorValue) ? drErrorValue : "0";
    } else if (response.data.responseMessage?.code === 2) {
      return "0";
    } else {
      return response.data.currencyRate;
    }
  } catch (error) {
    DRLog("ERROR", "CurrencyRate error" + error);
    throw error;
  }
}

export async function UnitCurrency(Dim_Id: string, Unit: string, Year: number): Promise<string> {
  try {
    const response = await axiosInstance.get("/api/DR/unitcurrency", {
      params: {
        dimId: Dim_Id,
        unit: Unit,
        year: Year,
      },
    });
    if (!isResponseValid(response)) {
      const drErrorValue = getDRErrorValue();
      return isValidString(drErrorValue) ? drErrorValue : "0";
    } else if (response.data.responseMessage?.code === 2) {
      return "0";
    } else {
      return response.data.unitCurrency;
    }
  } catch (error) {
    DRLog("ERROR", "UnitCurrency error" + error);
    throw error;
  }
}

export async function UnitName(Dim_Id: string, Unit: string, Year: number, Language_Code: string): Promise<string> {
  try {
    const response = await axiosInstance.get("/api/DR/drunitdata", {
      params: {
        dimId: Dim_Id,
        year: Year,
      },
    });
    if (!isResponseValid(response)) {
      const drErrorValue = getDRErrorValue();
      return isValidString(drErrorValue) ? drErrorValue : "0";
    } else if (response.data.responseMessage?.code === 2) {
      return "0";
    } else {
      // Find the unit by unitId and return the name for Language_Code
      const units = response.data.DrUnitData;
      if (Array.isArray(units)) {
        const normalizedUnit = String(Unit).toUpperCase();
        const unitObj = units.find(
          (u: any) => String(u.unitId).toUpperCase() === normalizedUnit
        );
        if (unitObj) {
          return unitObj.LocalizationInfo?.[Language_Code] || "0";
        }
      }
      return "0";
    }
  } catch (error) {
    DRLog("ERROR", "UnitName error" + error);
    throw error;
  }
}

export async function ConfigFileNames(): Promise<string[]> {
  try {
    const response = await axiosInstance.get("/api/DR/configfilenames");
    return response.data.fileNames;
  } catch (error) {
    DRLog("ERROR", "ConfigFileNames error" + error);
    return [CustomFunctions.ErrorCode.invalidValue];
  }
}

export async function httpAddress(configName: string): Promise<string> {
  try {
    const response = await axiosInstance.get("/api/DR/httpaddress", {
      params: {
        configName: configName,
      },
    });
    return response.data.httpAddress;
  } catch (error) {
    DRLog("ERROR", "httpAddress error" + error);
    throw error;
  }
}

export async function accountData(): Promise<string> {
  try {
    const response = await axiosInstance.get("/api/DR/draccountdata");
    if (!isResponseValid(response)) {
      const drErrorValue = getDRErrorValue();
      return isValidString(drErrorValue) ? drErrorValue : "0";
    } else if (response.data.responseMessage?.code === 2) {
      return "0";
    } else {
      return response.data.DrAccountData;
    }
  } catch (error) {
    DRLog("ERROR", "accountData error" + error);
    throw error;
  }
}

export async function Data(requestData: string, accounts: string[], timeout: number): Promise<string> {
  try {
    const response = await axiosInstance.post(
      "/api/DR/drdata",
      {
        RequestData: requestData,
        Accounts: accounts,
      },
      {
        timeout: timeout,
      }
    );
    if (!isResponseValid(response)) {
      const drErrorValue = getDRErrorValue();
      return isValidString(drErrorValue) ? drErrorValue : "0";
    } else if (response.data.responseMessage?.code === 2) {
      return null;
    } else {
      return response.data.ResultData;
    }
  } catch (error) {
    DRLog("ERROR", "Data error" + error);
    throw error;
  }
}

export async function ExtraData(
  requestData: string,
  accounts: string[],
  extraFieldIds: string[],
  timeout: number
): Promise<string> {
  try {
    const response = await axiosInstance.post(
      "/api/DR/drextradata",
      {
        RequestData: requestData,
        Accounts: accounts,
        ExtraFields: extraFieldIds,
      },
      {
        timeout: timeout,
      }
    );
    if (!isResponseValid(response)) {
      const drErrorValue = getDRErrorValue();
      return isValidString(drErrorValue) ? drErrorValue : "0";
    } else if (response.data.responseMessage?.code === 2) {
      return null;
    } else {
      return response.data.ResultData;
    }
  } catch (error) {
    DRLog("ERROR", "ExtraData error" + error);
    throw error;
  }
}
